import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import axios from "axios";
import * as moment from "moment";
import dayjs, { Dayjs } from "dayjs";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";

import { Button, TextInput, Dropdown, Image } from "hesus-ui-kit";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import CKEditorText from "@components/CKEditorText/CKEditorText";
import { loadSingleCompany as addSingleCompany } from "../../../CompanyList/CompanyList.slice";
import AddEditWorkingHours from "../../../../WorkingHours/AddEditWorkingHours";
import { addCentreRecyclingCodes } from "@shared/shared.slice";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import errorIcon from "@icons/ic-error.svg";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
  RequiredMessage,
} from "@utils/extra-style";
import "@utils/extra-style.scss";
import UserInfo from "@utils/UserInfo";
import {
  ButtonWrapper,
  FormBody,
  SectionContainer,
  OuterWrapper,
  FlexRow,
  GridRow,
  OuterWrapperBackground,
  PaddingDiv,
  ContainerLabel,
  ErrorDiv,
  AccessLabel,
} from "./Centre.styled";
import "../../AddEditCompany.scss";

const Centre = (props) => {
  const {
    t,
    id,
    selectedCompany,
    setSelectedCompany,
    isEditMode,
    setActiveTab,
    leadId,
  } = props;

  const dispatch = useDispatch();
  const history = useNavigate();
  const generalParams = useGetGeneralParameters();
  const params = useParams();
  const [isCompanyCreating, setIsCompanyCreating] = useState(false);
  const [createCompanyAPIError, setCreateCompanyAPIError] = useState("");
  const [descriptionData, setDescriptionData] = useState("");
  let companyId = parseInt(params.id, 10);

  const [inputList, setInputList] = useState([
    {
      id: "",
      openingTime: dayjs("0000-00-00T07:00"),
      closingTime: dayjs("0000-00-00T16:00"),
      days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    },
  ]);

  const [inputListError, setInputListError] = useState([
    {
      timeError: "",
    },
  ]);
  const [requiredTimeData, setRequiredTimeData] = useState("");

  const { centreRecyclingCodes } = generalParams;

  const countryId = selectedCompany?.platformCountryId ?? UserInfo.countryId();

  if (selectedCompany?.id) companyId = selectedCompany.id;

  const [formData, setFormData] = useState({
    logisticFlow: {
      required: true,
      value: selectedCompany?.centre?.logisticFlow ?? "",
      error: "",
    },
    isBackfill: {
      required: true,
      value: selectedCompany?.centre?.isBackfill ?? false,
      error: "",
    },
    loadingTime: {
      required: true,
      value: selectedCompany?.centre?.loadingTime ?? 15,
      error: "",
    },
    unloadingTime: {
      required: true,
      value: selectedCompany?.centre?.unloadingTime ?? 15,
      error: "",
    },
    simultaneousLoads: {
      required: true,
      value: selectedCompany?.centre?.simultaneousLoads ?? 1,
      error: "",
    },
    simultaneousUnloads: {
      required: true,
      value: selectedCompany?.centre?.simultaneousUnloads ?? 1,
      error: "",
    },
    isRoadAccess: {
      required: false,
      value: selectedCompany?.centre?.isRoadAccess ?? true,
      error: "",
    },
    isTrainAccess: {
      required: false,
      value: selectedCompany?.centre?.isTrainAccess ?? false,
      error: "",
    },
    isBoatAccess: {
      required: false,
      value: selectedCompany?.centre?.isBoatAccess ?? false,
      error: "",
    },
    recyclingCode: {
      required: false,
      value: selectedCompany?.centre?.recyclingCode?.id ?? "",
      error: "",
    },
    isAppointmentForDelivery: {
      required: false,
      value: selectedCompany?.centre?.isAppointmentForDelivery ?? false,
      error: "",
    },
    extendedHours: {
      required: false,
      value: selectedCompany?.centre?.isExtendedHours ?? false,
      error: "",
    },
    documentsDelays: {
      required: false,
      value: selectedCompany?.centre?.documentsDelays ?? "",
      error: "",
    },
    extendedHoursDetails: {
      required: false,
      value: selectedCompany?.centre?.extendedHoursDetails ?? "",
      error: "",
    },
    documentSpecificities: {
      required: false,
      value: selectedCompany?.centre?.documentsSpecificities ?? "",
      error: "",
    },
    comment: {
      required: false,
      value: selectedCompany?.centre?.comment ?? "",
      error: "",
    },
    workingHours: {
      required: false,
      value: selectedCompany?.centre?.workingHours ?? [],
      error: "",
    },
  });

  // get parameters
  const fetchRecyclingCodes = () => {
    axios
      .get(`/general/centreRecyclingCodes?countryId=${countryId}`)
      .then((response) => {
        dispatch(addCentreRecyclingCodes(response.data));
      })
      .catch((error) => {});
  };

  useEffect(() => {
    if (!centreRecyclingCodes || centreRecyclingCodes?.length === 0) {
      fetchRecyclingCodes();
    }
  }, []);

  useEffect(() => {
    setFormData({
      ...formData,
      comment: {
        value: descriptionData,
        error: "",
        required: true,
      },
    });
  }, [descriptionData]);

  const onLogisticTypeChange = (logisticFlow) => {
    setFormData({
      ...formData,
      logisticFlow: {
        ...formData.logisticFlow,
        error: "",
        value: logisticFlow.value ? parseInt(logisticFlow.value, 10) : "",
      },
    });
  };

  const onRecyclingCodeChange = (code) => {
    setFormData({
      ...formData,
      recyclingCode: {
        ...formData.recyclingCode,
        error: "",
        value: code.value ? parseInt(code.value, 10) : "",
      },
    });
  };

  const validateForm = (data, inputList, inputListError) => {
    let isFormValid = true;
    if (!data.logisticFlow.value) {
      isFormValid = false;
      data.logisticFlow.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (
      !data.isRoadAccess.value &&
      !data.isTrainAccess.value &&
      !data.isBoatAccess.value
    ) {
      isFormValid = false;
      data.isRoadAccess.error = t("FIELD_REQUIRED");
    }
    const listError = [...inputListError];
    const errorFieldName = "timeError";
    if (inputList.length > 0) {
      inputList.map((input, index) => {
        if (
          input.openingTime.hour() === "" ||
          input.openingTime.minute() === "" ||
          input.closingTime.hour() === "" ||
          input.closingTime.minute() === "" ||
          input.days.length === 0
        ) {
          isFormValid = false;
          listError[index][errorFieldName] = t(
            "WORKING_HOURS_MISSING_INFORMATION"
          );
          setRequiredTimeData("WORKING_HOURS_MISSING_INFORMATION");
        }
      });
      setInputListError(listError);
    }

    if (inputList.length === 0) {
      isFormValid = false;
      setRequiredTimeData("COMPANY_ERROR_MISSING_WORKING_HOURS");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }
    return isFormValid;
  };

  const getFormData = () => {
    let workingHoursData = [];
    if (inputList.length > 0) {
      inputList.map((inputData) => {
        const newOpeningMint =
          parseInt(inputData.openingTime.minute(), 10) < 10
            ? "0" + inputData.openingTime.minute()
            : inputData.openingTime.minute();
        const newClosingMint =
          parseInt(inputData.closingTime.minute(), 10) < 10
            ? "0" + inputData.closingTime.minute()
            : inputData.closingTime.minute();
        workingHoursData.push({
          id: inputData.id,
          centreId: isEditMode ? selectedCompany?.centre?.id : "",
          openingHour: inputData.openingTime.hour() + ":" + newOpeningMint,
          closingHour: inputData.closingTime.hour() + ":" + newClosingMint,
          days: inputData.days.join(),
        });
      });
    }
    const data = {
      id: isEditMode ? selectedCompany?.centre?.id : "",
      logisticFlow: formData.logisticFlow.value,
      isBackfill: formData.isBackfill.value,
      platformCountryId: countryId,
      loadingTime: parseInt(formData.loadingTime.value, 10),
      unloadingTime: parseInt(formData.unloadingTime.value, 10),
      simultaneousLoads: parseInt(formData.simultaneousLoads.value, 10),
      simultaneousUnloads: parseInt(formData.simultaneousUnloads.value, 10),
      isAppointmentForDelivery: formData.isAppointmentForDelivery.value,
      isBoatAccess: formData.isBoatAccess.value,
      isExtendedHours: formData.extendedHours.value,
      isRoadAccess: formData.isRoadAccess.value,
      isTrainAccess: formData.isTrainAccess.value,
      comment: formData.comment.value,
      documentsDelays: formData.documentsDelays.value,
      documentsSpecificities: formData.documentSpecificities.value,
      extendedHoursDetails: formData.extendedHoursDetails.value,
      recyclingCode: formData.recyclingCode.value
        ? { id: formData.recyclingCode.value }
        : null,
      workingHours: workingHoursData,
      isUpdate: true,
    };
    return data;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData, inputList, inputListError);
    if (isFormValid) {
      setIsCompanyCreating(true);
      setCreateCompanyAPIError("");
      const centreData = { ...selectedCompany.centre, ...getFormData() };
      const companyData = { ...selectedCompany, centre: { ...centreData } };
      axios
        .put(`/companies/${companyId}`, companyData)
        .then((response) => {
          setIsCompanyCreating(false);
          setSelectedCompany(response?.data[0]);
          dispatch(addSingleCompany(response.data));
          if (response?.data[0].isHaulier) setActiveTab("haulier");
          else if (leadId) history(`/lead/view/${leadId}`);
          else history(`/company/view/${response?.data[0].id}/general-info`);
        })
        .catch((error) => {
          setIsCompanyCreating(false);
          setCreateCompanyAPIError(error.response?.data?.message ?? "");
        });
    }
  };

  const onCancel = () => {
    if (leadId) history(`/lead/view/${leadId}`);
    else history(`/company/view/${companyId}/general-info`);
  };

  return (
    <OuterWrapperBackground>
      <PaddingDiv>
        <FormBody>
          <OuterWrapper>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow column="1fr 1fr">
                    <Dropdown
                      id="centre-logistic-flow"
                      label={t("LOGISTIC_FLOW")}
                      name="centre-logistic-flow"
                      value={formData.logisticFlow.value}
                      defaultOption={t("NONE_SELECTED")}
                      options={[
                        { label: t("LOGISTIC_FLOW_ROUND_TRIP"), value: 1 },
                        { label: t("LOGISTIC_FLOW_RETURN_LOAD"), value: 2 },
                        { label: t("LOGISTIC_FLOW_BOTH"), value: 3 },
                      ]}
                      required
                      containerStyles={{ marginBottom: "1.32rem" }}
                      onChange={onLogisticTypeChange}
                      labelStyles={
                        formData.logisticFlow.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.logisticFlow.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.logisticFlow.error}
                    />
                    <div>
                      <div style={{ lineHeight: "84px" }}>
                        <Image
                          src={
                            formData.isBackfill.value ? enableIcon : disableIcon
                          }
                          alt={t("CENTRE_IS_BACKFILL")}
                          title={t("CENTRE_IS_BACKFILL")}
                          styles={{ marginRight: "10px" }}
                          onClick={() => {
                            setFormData({
                              ...formData,
                              isBackfill: {
                                value: !formData.isBackfill.value,
                                error: "",
                                required: true,
                              },
                            });
                          }}
                        />
                        <label className="label center-products-list__active-label">
                          {t("CENTRE_IS_BACKFILL")}
                        </label>
                      </div>
                    </div>
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <GridRow column="1fr 1fr">
                      <div>
                        <TextInput
                          type="text"
                          label={t("LOADING_TIME")}
                          name="centre-loading-time"
                          id="centre-loading-time"
                          value={String(formData.loadingTime?.value) || ""}
                          isFloat
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              loadingTime: {
                                value: e.target.value,
                                error: "",
                                required: true,
                              },
                            });
                          }}
                          labelStyles={labelStyles}
                          containerStyles={{ marginTop: "21px" }}
                          inputStyles={{ padding: "7px 5px 7px 10px" }}
                          error={formData.loadingTime.error}
                          placeholder={t("LOADING_TIME")}
                        />
                        <label className="label center-products-list__active-label">
                          min.
                        </label>
                      </div>

                      <div>
                        <TextInput
                          type="text"
                          label={t("UNLOADING_TIME")}
                          name="centre-unloading-time"
                          id="centre-unloading-time"
                          value={String(formData.unloadingTime?.value) || ""}
                          isFloat
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              unloadingTime: {
                                value: e.target.value,
                                error: "",
                                required: true,
                              },
                            });
                          }}
                          labelStyles={labelStyles}
                          containerStyles={{ marginTop: "21px" }}
                          inputStyles={{ padding: "7px 5px 7px 10px" }}
                          error={formData.unloadingTime.error}
                          placeholder={t("UNLOADING_TIME")}
                        />
                        <label className="label center-products-list__active-label">
                          min.
                        </label>
                      </div>
                    </GridRow>
                    <GridRow column="1fr 1fr">
                      <TextInput
                        type="text"
                        label={t("SIMULTANEOUS_LOADS")}
                        name="centre-simultaneous-loads"
                        id="centre-simultaneous-loads"
                        value={String(formData.simultaneousLoads?.value) || ""}
                        isFloat
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            simultaneousLoads: {
                              value: e.target.value,
                              error: "",
                              required: true,
                            },
                          });
                        }}
                        labelStyles={labelStyles}
                        containerStyles={{ marginTop: "21px" }}
                        inputStyles={{ padding: "7px 5px 7px 10px" }}
                        error={formData.simultaneousLoads.error}
                        placeholder={t("COMPANY_PHONE")}
                      />
                      <TextInput
                        type="text"
                        label={t("SIMULTANEOUS_UNLOADS")}
                        name="centre-simultaneous-unloads"
                        id="centre-simultaneous-unloads"
                        value={
                          String(formData.simultaneousUnloads?.value) || ""
                        }
                        isFloat
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            simultaneousUnloads: {
                              value: e.target.value,
                              error: "",
                              required: true,
                            },
                          });
                        }}
                        labelStyles={labelStyles}
                        containerStyles={{ marginTop: "21px" }}
                        inputStyles={{ padding: "7px 5px 7px 10px" }}
                        error={formData.simultaneousUnloads.error}
                        placeholder={t("SIMULTANEOUS_UNLOADS")}
                      />
                    </GridRow>
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <div>
                      <ContainerLabel
                        htmlFor="access"
                        labelStyles={
                          formData.isRoadAccess.error !== ""
                            ? labelStylesError
                            : labelStyles
                        }
                      >
                        {t("CENTRE_ACCESSES")}
                      </ContainerLabel>
                      <GridRow column="1fr 1fr 1fr">
                        <div>
                          <Image
                            src={
                              formData.isRoadAccess.value
                                ? enableIcon
                                : disableIcon
                            }
                            alt={t("CENTRE_IS_ROAD_ACCESS")}
                            title={t("CENTRE_IS_ROAD_ACCESS")}
                            styles={{ marginRight: "10px" }}
                            onClick={() => {
                              setFormData({
                                ...formData,
                                isRoadAccess: {
                                  value: !formData.isRoadAccess.value,
                                  error: "",
                                  required: true,
                                },
                              });
                            }}
                          />
                          <AccessLabel>{t("ROAD_ACCESS")}</AccessLabel>
                        </div>

                        <div>
                          <Image
                            src={
                              formData.isTrainAccess.value
                                ? enableIcon
                                : disableIcon
                            }
                            alt={t("CENTRE_IS_RAILWAY_ACCESS")}
                            title={t("CENTRE_IS_RAILWAY_ACCESS")}
                            styles={{ marginRight: "10px" }}
                            onClick={() => {
                              setFormData({
                                ...formData,
                                isTrainAccess: {
                                  value: !formData.isTrainAccess.value,
                                  error: "",
                                  required: true,
                                },
                              });
                            }}
                          />
                          <AccessLabel>{t("RAILWAY_ACCESS")}</AccessLabel>
                        </div>

                        <div>
                          <Image
                            src={
                              formData.isBoatAccess.value
                                ? enableIcon
                                : disableIcon
                            }
                            alt={t("CENTRE_IS_RIVER_ACCESS")}
                            title={t("CENTRE_IS_RIVER_ACCESS")}
                            styles={{ marginRight: "10px" }}
                            onClick={() => {
                              setFormData({
                                ...formData,
                                isBoatAccess: {
                                  value: !formData.isBoatAccess.value,
                                  error: "",
                                  required: true,
                                },
                              });
                            }}
                          />
                          <AccessLabel>{t("RIVER_ACCESS")}</AccessLabel>
                        </div>
                      </GridRow>
                    </div>
                    {centreRecyclingCodes?.length > 0 && (
                      <Dropdown
                        id="centre-recycling-code"
                        label={t("CENTRE_RECYCLING_CODE")}
                        name="centre-recycling-code"
                        value={formData.recyclingCode.value}
                        onChange={onRecyclingCodeChange}
                        defaultOption={t("CENTRE_RECYCLING_CODE")}
                        options={Object.values(centreRecyclingCodes)}
                        labelStyles={labelStyles}
                        inputStyles={inputStyles}
                        containerStyles={{ marginTop: "21px" }}
                        error={formData.recyclingCode.error}
                      />
                    )}
                  </GridRow>
                  {formData.isRoadAccess.error !== "" && (
                    <GridRow column="1fr 1fr">
                      <ErrorDiv>
                        <RequiredMessage maxWidth="auto">
                          <Image
                            src={errorIcon}
                            alt="warning"
                            styles={{ marginRight: "10px", width: "18px" }}
                          />{" "}
                          {t("COMPANY_ERROR_CENTRE_ACCESSES_MISSING")}{" "}
                        </RequiredMessage>
                      </ErrorDiv>
                    </GridRow>
                  )}
                  <AddEditWorkingHours
                    inputList={inputList}
                    inputListError={inputListError}
                    setInputList={setInputList}
                    setInputListError={setInputListError}
                    requiredTimeData={requiredTimeData}
                    setRequiredTimeData={setRequiredTimeData}
                    workingHours={
                      formData.workingHours.value
                        ? formData.workingHours.value
                        : []
                    }
                  />
                  <GridRow column="1fr 1fr">
                    <div>
                      <Image
                        src={
                          formData.isAppointmentForDelivery.value
                            ? enableIcon
                            : disableIcon
                        }
                        alt={t("CENTRE_APPOINTMENT_FOR_DELIVERY")}
                        title={t("CENTRE_APPOINTMENT_FOR_DELIVERY")}
                        styles={{ marginRight: "10px" }}
                        onClick={() => {
                          setFormData({
                            ...formData,
                            isAppointmentForDelivery: {
                              value: !formData.isAppointmentForDelivery.value,
                              error: "",
                              required: true,
                            },
                          });
                        }}
                      />
                      <label className="label center-products-list__active-label">
                        {t("CENTRE_APPOINTMENT_FOR_DELIVERY")}
                      </label>
                    </div>
                    <div>
                      <Image
                        src={
                          formData.extendedHours.value
                            ? enableIcon
                            : disableIcon
                        }
                        alt={t("CENTRE_EXTENDED_HOURS")}
                        title={t("CENTRE_EXTENDED_HOURS")}
                        styles={{ marginRight: "10px" }}
                        onClick={() => {
                          setFormData({
                            ...formData,
                            extendedHours: {
                              value: !formData.extendedHours.value,
                              error: "",
                              required: true,
                            },
                          });
                        }}
                      />
                      <label className="label center-products-list__active-label">
                        {t("CENTRE_EXTENDED_HOURS")}
                      </label>
                    </div>
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <TextInput
                      type="text"
                      label={t("DOCUMENTS_DELAY")}
                      name="centre-simultaneous-loads"
                      id="centre-simultaneous-loads"
                      value={formData.documentsDelays?.value || ""}
                      isFloat
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          documentsDelays: {
                            value: e.target.value,
                            error: "",
                            required: true,
                          },
                        });
                      }}
                      labelStyles={labelStyles}
                      containerStyles={{ marginTop: "21px" }}
                      inputStyles={{ padding: "7px 5px 7px 10px" }}
                      error={formData.documentsDelays.error}
                      placeholder={t("DOCUMENTS_DELAY")}
                    />
                    <TextInput
                      type="text"
                      label={t("EXTENDED_HOURS_DETAIL")}
                      name="centre-simultaneous-unloads"
                      id="centre-simultaneous-unloads"
                      value={formData.extendedHoursDetails?.value || ""}
                      isFloat
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          extendedHoursDetails: {
                            value: e.target.value,
                            error: "",
                            required: true,
                          },
                        });
                      }}
                      labelStyles={labelStyles}
                      containerStyles={{ marginTop: "21px" }}
                      inputStyles={{ padding: "7px 5px 7px 10px" }}
                      error={formData.extendedHoursDetails.error}
                      placeholder={t("EXTENDED_HOURS_DETAIL")}
                    />
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <TextInput
                      type="text"
                      label={t("DOCUMENTS_SPEC")}
                      name="centre-simultaneous-unloads"
                      id="centre-simultaneous-unloads"
                      value={formData.documentSpecificities?.value || ""}
                      isFloat
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          documentSpecificities: {
                            value: e.target.value,
                            error: "",
                            required: true,
                          },
                        });
                      }}
                      labelStyles={labelStyles}
                      containerStyles={{ marginTop: "21px" }}
                      inputStyles={{ padding: "7px 5px 7px 10px" }}
                      error={formData.documentSpecificities.error}
                      placeholder={t("DOCUMENTS_SPEC")}
                    />
                  </GridRow>
                  <GridRow column="1fr">
                    <div>
                      <ContainerLabel htmlFor="description">
                        {t("ADDITIONAL_INFORMATION")}
                      </ContainerLabel>
                      <CKEditorText
                        descriptionData={descriptionData}
                        setDescriptionData={setDescriptionData}
                      />
                      {formData.comment.error && (
                        <RequiredMessage>
                          {formData.comment.error}
                        </RequiredMessage>
                      )}
                    </div>
                  </GridRow>
                </div>
              </SectionContainer>
            </FlexRow>
          </OuterWrapper>
        </FormBody>
        <ButtonWrapper>
          {createCompanyAPIError && <div>{createCompanyAPIError}</div>}
          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onCancel}
          >
            <>{t("CANCEL")}</>
          </Button>

          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isCompanyCreating && <ClipLoader size={15} color="#fff" />}
              {!isCompanyCreating && t("SUBMIT")}
            </>
          </Button>
        </ButtonWrapper>
      </PaddingDiv>
    </OuterWrapperBackground>
  );
};
Centre.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  isEditMode: PropTypes.bool,
  setTypeData: PropTypes.func.isRequired,
  typeData: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
};
Centre.defaultProps = {
  id: null,
  isEditMode: false,
};

export default withTranslation()(Centre);
